/**
 * Dialog.js — the one modal/drawer primitive.
 *
 * Gives every overlay the same contract (IMPLEMENTATION-SPEC "Modal" and
 * "Drawer" rows): dialog semantics, a labelled title, focus moved to the first
 * meaningful control on open, Tab trapped while open, Escape and backdrop
 * routed through `onClose`, and focus restored to the trigger on close.
 *
 * `onClose` is the single dismissal path. Callers that must not discard work
 * (dirty forms, running mutations) decide inside onClose whether to close —
 * Dialog never unmounts itself.
 *
 *   <Dialog open title="New project" onClose={...} footer={<>…buttons…</>}>
 *     …body…
 *   </Dialog>
 *
 * variant="drawer" slides from the right (full width below 768px).
 * modal={false} renders a non-modal drawer: no backdrop, no focus trap, stays
 * in the keyboard order — used for inspectors that must not block the page.
 */
import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';

const FOCUSABLE_LIST = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
];
const FOCUSABLE = FOCUSABLE_LIST.join(',');
// Scope every selector in the list, not just the first one.
const within = (scope) => FOCUSABLE_LIST.map(sel => `${scope} ${sel}`).join(',');

// Only the top-most open dialog handles Escape / Tab, so a confirm opened on
// top of a drawer doesn't close both.
const stack = [];

export default function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  variant = 'dialog',
  modal = true,
  maxWidth,
  wide = false,
  initialFocusRef,
  closeLabel = 'Close',
  closeOnBackdrop = true,
  className = '',
}) {
  const panelRef = useRef(null);
  const titleId = useId();
  const descId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const trigger = document.activeElement;
    const token = {};
    stack.push(token);

    // Focus the first meaningful control: explicit ref, else the first field
    // or button in the body, else the panel itself. The close button is last
    // in DOM order for this reason.
    const panel = panelRef.current;
    const target = initialFocusRef?.current
      || panel?.querySelector(within('.dialog-body'))
      || panel?.querySelector(within('.dialog-foot'))
      || panel;
    target?.focus({ preventScroll: true });

    const onKey = (e) => {
      if (stack[stack.length - 1] !== token) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab' || !modal || !panelRef.current) return;
      const items = [...panelRef.current.querySelectorAll(FOCUSABLE)].filter(el => el.offsetParent !== null);
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);

    return () => {
      document.removeEventListener('keydown', onKey, true);
      const i = stack.indexOf(token);
      if (i >= 0) stack.splice(i, 1);
      if (trigger && typeof trigger.focus === 'function' && document.contains(trigger)) {
        trigger.focus({ preventScroll: true });
      }
    };
    // initialFocusRef is read once on open by design.
  }, [open, modal]);

  if (!open) return null;

  const isDrawer = variant === 'drawer';
  const panelClass = isDrawer
    ? `drawer ${wide ? 'drawer--wide' : ''} ${className}`
    : `dialog ${className}`;

  const panel = (
    <div
      ref={panelRef}
      className={panelClass}
      role="dialog"
      aria-modal={modal ? 'true' : undefined}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      tabIndex={-1}
      style={maxWidth ? { maxWidth } : undefined}
      onMouseDown={e => e.stopPropagation()}
    >
      <div className="dialog-head">
        <div style={{ minWidth: 0 }}>
          <h2 id={titleId} className="dialog-title">{title}</h2>
          {description && <p id={descId} className="dialog-desc">{description}</p>}
        </div>
        <button type="button" className="icon-btn" onClick={() => onClose?.()} aria-label={closeLabel}>
          <Icon name="close" />
        </button>
      </div>
      <div className="dialog-body">{children}</div>
      {footer && <div className="dialog-foot">{footer}</div>}
    </div>
  );

  if (!modal) return createPortal(panel, document.body);

  return createPortal(
    isDrawer ? (
      <>
        <div className="drawer-backdrop" onMouseDown={() => closeOnBackdrop && onClose?.()} />
        {panel}
      </>
    ) : (
      <div className="dialog-backdrop" onMouseDown={() => closeOnBackdrop && onClose?.()}>
        {panel}
      </div>
    ),
    document.body,
  );
}
